import React, { useEffect, useState, useCallback, useRef } from "react";
import { CombinationAlternativeModal } from "./CombinationAlternativeModal";
import {
  ON_CHANGE_STEP_THREE_INTERNAL_DATA,
  ON_COMBINE_ALTERNATIVES,
  ON_OPEN_SEND_ALTERNATIVE_MODAL,
  ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS,
  ON_UPDATE_STEP_THREE_DATA,
} from "src/hooks/EventBus/eventTypes";
import { useEventBus } from "src/hooks";
import Tooltip from "../components/ToolTip";
import GenericCollapsibleList from "../sections/GenericCollapsibleList";
import * as Ui from "./styles";
import {
  alternativesToGenericModel,
  getGroupQuestionsPayload,
} from "../parsers";
import { useDispatch, useSelector } from "react-redux";
import { fetchAlternativesByGroupQuestions } from "src/stores/actions/evolutiveCross";
import { calculateArrayHashCode, isNullOrEmptyArray } from "src/util/arrays";
import * as GenericUi from "../components/Modal/styles";
import { ChainIcon, MatchIcon } from "src/components/Icons";
import { SendAlternativesCombinationModal } from "./SendCombinationModal";
import { ListAlMenuIcon } from "src/components";
import { MatchedIcon } from "src/components/Icons";
import { getAlternativeIdentifiers } from "../util";
import { genericModalDataToAlternativeData, mapAlternativesToSurveyIdList } from "../parsers/alternatives";
import { StepManager } from "../managers";
import useFlashMessage from "src/hooks/FlashMessage";
import {
  ALTERNATIVE_SENT,
  COMBINE_ALTERNATIVES_WITH_SAME_SURVEYID,
  EVOLUTIVE_CROSSING_NETWORK_ERROR,
  SEND_ALTERNATIVES_WITH_SAME_SURVEYID,
} from "src/sections/GroupCards/EvolutiveCrossChartCard/config/util/Constantes";
import { handleOnExecuteCombinationActions } from "../actionHandler";

// Item props
const itemProps = {
  width: "100%",
  withTag: true,
  maxHeight: "unset",
  renderStyle: "stepThreeList",
};

function StepThree({
  // Relacionado a visibilidade do passo
  visible = false,
  // Id do cliente
  customerId,
  // Id da pesquisa principal
  surveyId,
  // função que recebe se todas as perguntas foram visualizadas
  handleAllViewed,
  // Outras propriedades
  ...props
}) {
  // States

  // Dados relacionados às alternativas combinadas para o grupo de perguntas principais e secundárias.
  const [internalData, setInternalData] = useState(null);
  // Estados de visibilidade das modais
  const [showCombinationModal, setShowCombinationModal] = useState(false);
  const [showSendAlternativeModal, setShowSendAlternativeModal] =
    useState(false);
  const [combinedQuestions, setCombinedQuestions] = useState([]);
  const [visualized, setVisualized] = useState([]);
  const [withData, setWithData] = useState(false);

  const [isInComingCombinedQuestions, setIsIncomingCombinedQuestions] =
    useState(false);
  // refs
  // Alternativas avulsas selecionadas
  const selectedAlternativesRef = useRef(null);
  // Ref para o componente GenericCollapsibleList, utilizado na listagem de alternativas avulsas.
  const alternativesListRef = useRef([]);

  // hooks
  const eventBus = useEventBus();
  const [showFlashMessage] = useFlashMessage();

  // redux
  const dispatch = useDispatch();

  const {
    running: resultRunning,
    payload: resultPayload,
    error: resultError,
  } = useSelector(
    (state) => state.evolutiveCross.configuration.step_three.groupAlternatives
  );

  /**
   * Effects: Subscribers
   */
  useEffect(() => {
    // Evento disparado na ação de clique de combinar alternativas
    eventBus.subscribe(ON_COMBINE_ALTERNATIVES, () => {
      const surveyIdList = mapAlternativesToSurveyIdList(selectedAlternativesRef.current);

      handleOnExecuteCombinationActions(
        surveyIdList,
        null,
        () => {
          setShowCombinationModal(true);
        },
        () => {
          const { title, message } = COMBINE_ALTERNATIVES_WITH_SAME_SURVEYID;
          showFlashMessage(title, message, 'warning');
        }
      );
    });

    // Evento disparado na ação de clique de enviar alternativa para um grupo pré-existente
    eventBus.subscribe(ON_OPEN_SEND_ALTERNATIVE_MODAL, () => {
      const surveyIdList = mapAlternativesToSurveyIdList(selectedAlternativesRef.current);

      handleOnExecuteCombinationActions(
        surveyIdList,
        null,
        () => {
          setShowSendAlternativeModal(true);
        },
        () => {
          const { title, message } = SEND_ALTERNATIVES_WITH_SAME_SURVEYID;
          showFlashMessage(title, message, 'warning');
        }
      );
    });
  }, []);

  /**
   * Callbacks: Calcula quais grupos de perguntas combinadas no passo 2 sofreram alterações
   * o retorno é dado por um array de groupId's, indicando quais grupos sofreram mudanças.
   */
  const getCombinedGroupsWithChanges = useCallback(
    (oldGroups = [], newGroups = []) => {
      return newGroups
        .filter((o) => {
          const findedOldGroup = oldGroups.find((v) => v.id === o.id);

          // Caso o grupo iterado atual em newGroups coexista nas perguntas combinadas antigas é feita uma verificação
          // em cima dos seus dados internos, representado pela propriedade "group", que é um array de perguntas.
          if (
            findedOldGroup &&
            calculateArrayHashCode(o.group) !==
              calculateArrayHashCode(findedOldGroup.group)
          )
            return true;
          return false;
        })
        .map((o) => o.id);
    },
    []
  );

  /**
   * Effects: Seta os valores iniciais para o passo 3.
   */
  useEffect(() => {
    eventBus.subscribe(ON_UPDATE_STEP_THREE_DATA, (initialData) => {
      if (!isNullOrEmptyArray(initialData)) {
        const newCombinedQuestionsHash = calculateArrayHashCode(initialData);
        const oldCombinedQuestionsHash =
          calculateArrayHashCode(combinedQuestions);

        // Só queremos atualizar o estado de combinedQuestions caso os dados plotados tenham mudado no passo 2.
        if (newCombinedQuestionsHash !== oldCombinedQuestionsHash) {
          setIsIncomingCombinedQuestions(true);

          // Recuperamos os identificadores dos grupos de perguntas combinadas que sofreram alterações
          const changes = getCombinedGroupsWithChanges(
            combinedQuestions,
            initialData
          );

          setCombinedQuestions(
            initialData.map((o) => ({
              ...o,
              group: [...o.group],
              changed: changes.includes(o.id),
            }))
          );
        }
      }
    });

    // Lógica necessária para indenficar quando o estado de combinedQuestions já foi atualizado.
    if (!isNullOrEmptyArray(combinedQuestions)) {
      setIsIncomingCombinedQuestions(false);
      setWithData(false);
    }
  }, [combinedQuestions]);

  /**
   * Notificando que os dados plotados no passo 3 devem ser persistidos na página principal (EvolutiveView)
   */
  useEffect(() => {
    if (!isNullOrEmptyArray(internalData)) {
      StepManager.updateStepData(2, [...internalData]);
    }
  }, [internalData]);

  /**
   * Callbacks: Retorna os grupos de perguntas combinadas pela sua divisão (principal ou secundária)
   */
  const getParsedGroups = useCallback(() => {
    const mapped =
      combinedQuestions.length &&
      combinedQuestions.map(({ id, title, group }) => ({
        id,
        mainTitle: `Alternativas referente a combinação das perguntas <b class="green">${
          title || group[0].mainTitle
        }</b>`,
        tagTitle: `${group.length} PERGUNTAS`,
      }));

    if (mapped) {
      mapped[0].tagTitle = "PERGUNTAS PRINCIPAIS - " + mapped[0].tagTitle;

      return {
        principal: [mapped[0]],
        secondary: mapped.slice(1),
      };
    }

    return {
      principal: [],
      secondary: [],
    };
  }, [combinedQuestions]);

  const { principal, secondary } = getParsedGroups();

  /**
   * Callbacks: Callback responsável por armazenar os itens que foram visualizados pelo usuário
   */
  const handleExpandItem = useCallback(
    (item) => {
      setVisualized((state) => {
        const newItem = item.filter((i) => !state.some((st) => st.id === i.id));
        return !!newItem.length ? [...state, ...newItem] : [...state];
      });
    },
    [handleAllViewed, visualized]
  );

  /**
   * Effects: Effect responsável por monitora o array de itens combinados e visualizados
   */
  useEffect(() => {
    if (handleAllViewed) handleAllViewed(secondary, visualized);
  }, [handleAllViewed, visualized]);

  /**
   * Callbacks: (CORE) - Retorna as alternativas combinadas e as alternativas avulsas
   * dado um id para a listagem de alternativas por grupo (retorno da requisição).
   */
  const getMatchedAlternativesByGroupId = useCallback(
    (groupId) => {
      const matched = [];
      const avulsas = [];

      if (!isNullOrEmptyArray(resultPayload)) {
        const group = resultPayload.find(
          ({ uuid_grupo }) => uuid_grupo === groupId
        );

        // Garantindo que group é válido
        if (group) {
          const alternativas = [...group.alternativas];
          const altsCopy = [...alternativas];

          altsCopy.forEach((alternativa) => {
            const current = alternativas.pop();

            if (current) {
              current.id_grupo = groupId;

              const matches = alternativas.filter(
                ({ id_alternativa }) =>
                  id_alternativa === current.id_alternativa
              );

              if (matches.length) {
                matches.forEach((item) => {
                  alternativas.splice(alternativas.indexOf(item), 1);
                  item.id_grupo = groupId;
                });
                matched.push([...matches, current]);
              } else {
                avulsas.push(current);
              }
            }
          });
        }
      }

      return { matched, avulsas };
    },
    [resultPayload, internalData]
  );

  /**
   * Effects: Dispara a action para setar as alternativas das perguntas combinadas, principais e secundárias.
   */
  useEffect(() => {
    if (!isNullOrEmptyArray(combinedQuestions)) {
      const parsedPayload = getGroupQuestionsPayload(combinedQuestions);

      fetchAlternativesByGroupQuestions(customerId, parsedPayload, dispatch);
    }
  }, [customerId, combinedQuestions, dispatch]);

  /**
   * Effects: Seta os dados iniciais de alternativas combinadas e avulsas para cada grupo.
   */
  useEffect(() => {
    const withPayload = !isNullOrEmptyArray(resultPayload);

    if (
      !withData &&
      withPayload &&
      !resultRunning &&
      !isInComingCombinedQuestions
    ) {
      const data = [];

      data.push(
        ...combinedQuestions.map(({ id, group, changed }) => {
          // Recuperamos o valor pré-existente em internalData
          const oldValue =
            internalData && internalData.find((v) => v.id === id);
          // Caso os dados advindos do passo 2 tenham mudado (changed: boolean) ou o valor anterior
          // não exista, o recálculo do match será feito.
          if (changed || !oldValue) {
            const { matched, avulsas } = getMatchedAlternativesByGroupId(id);
            let id_pergunta_correspondente = null;

            // Encontra a pergunta principal e seta como identificador
            // para uso posterior na criação da configuração.
            group.forEach(({ id: sqid }) => {
              const [sid, qid] = sqid.split("_");

              if (sid == surveyId) {
                id_pergunta_correspondente = Number(qid);
              }
            });

            return { id, matched, avulsas, id_pergunta_correspondente };
          }

          // Caso contrário, se não existirem mudanças, o valor anterior em internalData será mantido.
          return { ...oldValue };
        })
      );

      setInternalData(data);
      setWithData(true);
    }
  }, [
    resultPayload,
    resultRunning,
    surveyId,
    combinedQuestions,
    internalData,
    isInComingCombinedQuestions,
    withData,
  ]);

  /**
   * Effects: error handling
   */
  useEffect(() => {
    if (resultError) {
      const { title, message } = EVOLUTIVE_CROSSING_NETWORK_ERROR;

      showFlashMessage(title, message, "error");
    }
  }, [resultError]);

  // Reseta a referencia das alternativas avulsas selecionadas.
  const resetSelectedAlternatives = function () {
    // Recupera a ultima instancia de listagem de alternativas avulsas modificada.
    const list =
      alternativesListRef.current &&
      alternativesListRef.current.find(({ id }) => id === getCurrentGroupId());

    if (list) {
      // Reseta a listagem
      list.instance.reset();
    }

    // Notifica que as opções do footer devem ser ocultadas
    eventBus.notify(ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS, {
      count: 0,
      visible: false,
      type: "alternatives",
    });
  };

  // Lida com o estado de enable / disabled do checkbox de cada alternativa avulsa.
  function handleDisabledAlternatives(currentSelection) {
    let list = null;
    let method = null;

    if (!isNullOrEmptyArray(currentSelection)) {
      // Id do grupo de alternativas atualmente manipulado.
      const { groupId } = getAlternativeIdentifiers(currentSelection[0].id);
      // Isolamos somente os grupos de alternativas que não fazem parte da seleção atual.
      list = alternativesListRef.current.filter(({ id }) => id !== groupId);
      // Método que será utilizado para habilitar ou desabilitar o estado de disabled da checkbox.
      method = "disableSelection";
    } else {
      // Se a seleção atual tiver length zero, significa que todos os grupos de alternativas são selecionáveis.
      list = alternativesListRef.current;
      method = "enableSelection";
    }

    // Aplicamos o método de enable/disable na referência da listagem correspondente.
    list.forEach(({ instance }) => instance[method]());
  }

  /**
   * Callbacks: Seta as alternativas avulsas atualmente selecionadas e notifica
   * a ação de renderização do footer options.
   */
  const handleOnSelectAlternatives = function (sel) {
    eventBus.notify(ON_RENDER_EVOLUTIVE_CROSS_FOOTER_OPTIONS, {
      count: sel.length,
      visible: !!sel.length,
      type: "alternatives",
    });

    // Setando as alternativas avulsas externalizadas
    selectedAlternativesRef.current = [...sel];
    // Habilita ou desabilitada as alternativas de outros grupos, dada a regra de negócio onde se estivermos manipulando
    // o checkbox de um grupo de alternativas avulsas, os demais grupos deve ter seu checkbox desabilitado.
    handleDisabledAlternatives(sel);
  };

  // Renderiza um componente referente ao conteúdo a direita do itens
  const RightContent = useCallback(
    (combinationLength, groupId) => {
      return (
        <Ui.RightContentLabel
          onClick={() => openEditCombinationModal(groupId)}
          withIcon
          className="rightContent"
        >
          <ChainIcon />
          {combinationLength} combinações
        </Ui.RightContentLabel>
      );
    },
    [internalData]
  );

  // Renderiza a contagem de alternativas combinadas
  const renderConditionalAlternativeProps = function (context) {
    const { count: combinationLength, id: groupId } = context;
    return {
      RightContent: RightContent.bind(null, combinationLength, groupId),
    };
  };

  /**
   * Parser: Faz o parse das alternativas combinadas e avulsas para
   * renderização correspondente ao figma na listagem.
   */
  const withParsedAlternatives = function (a) {
    let newData = null;

    if (!Array.isArray(a[0])) {
      newData = a;
    } else {
      newData = a.map((arr) => ({ ...arr[0], count: arr.length }));
    }

    return alternativesToGenericModel(newData);
  };

  // Retorna as informações de alternativas combinadas, avulsas e um índice do objeto
  // de referencia de acordo com o id do grupo.
  function getParsedAlternativesByGroupId(groupId) {
    const allGroups = [...internalData];
    const groupIndex = allGroups.findIndex((item) => item.id === groupId);

    // Solução necessária para o caso onde ainda não existe um grupo em internalData com id igual ao
    // id de parâmetro.
    if (groupIndex === -1) {
      return {
        groupIndex,
        matched: [],
        avulsas: [],
      };
    }

    return {
      // Indice do grupo
      groupIndex,
      // Alternativas combinadas
      matched: allGroups[groupIndex].matched,
      // Alternativas avulsas
      avulsas: allGroups[groupIndex].avulsas,
    };
  }

  function getCurrentGroupId() {
    const alternatives = selectedAlternativesRef.current;

    if (isNullOrEmptyArray(alternatives)) return null;
    const { groupId } = getAlternativeIdentifiers(alternatives[0].id);
    return groupId;
  }

  /**
   * Callbacks: Renderiza um label com texto genérico caso não exista alternativas
   * (avulsas ou combinadas) para exibir
   */
  const renderEmptyAlternatives = useCallback((title) => {
    return (
      <Ui.EmptyAlternatives>
        <Ui.EmptyLabel>{title}</Ui.EmptyLabel>
      </Ui.EmptyAlternatives>
    );
  }, []);

  /**
   * Callbacks: (CORE): Renderiza a lista de alternativas combinadas e a lista de alternativas avulsas.
   */
  const renderAlternativesList = useCallback(
    ({ id: groupId }) => {
      if (internalData) {
        const { matched, avulsas } = getParsedAlternativesByGroupId(groupId);

        return (
          <Ui.CombinedList>
            <p>
              <MatchedIcon /> Alternativas combinadas:
            </p>
            {isNullOrEmptyArray(matched) ? (
              renderEmptyAlternatives("Sem item combinado")
            ) : (
              <GenericCollapsibleList
                data={withParsedAlternatives(matched)}
                customItemProps={{
                  ...itemProps,
                  tagTitle: undefined,
                  renderStyle: "stepThreeCombinedAlternatives",
                }}
                itemConditionalRenderer={renderConditionalAlternativeProps}
              />
            )}

            <p>
              <MatchIcon /> Alternativas avulsas:
            </p>
            {isNullOrEmptyArray(avulsas) ? (
              renderEmptyAlternatives("Sem item avulso")
            ) : (
              <GenericCollapsibleList
                data={withParsedAlternatives(avulsas)}
                withControlledItems
                onChangeSelection={handleOnSelectAlternatives}
                customItemProps={{
                  ...itemProps,
                  checkboxStyle: "square",
                  renderStyle: "stepThreeSeparateAlternatives",
                }}
                // Callback para setar a instância da listagem quando o componente estiver montado.
                onReady={(instance) => {
                  alternativesListRef.current.push({ id: groupId, instance });
                }}
              />
            )}
          </Ui.CombinedList>
        );
      }

      return <></>;
    },
    [internalData, alternativesListRef, selectedAlternativesRef]
  );

  function onCloseCombinationModal() {
    resetSelectedAlternatives();
    setShowCombinationModal(false);
  }

  const onSaveCombinationAlternatives = useCallback(
    ({ alternatives }, removedAlternatives) => {
      const alternativeId = alternatives.length
        ? alternatives[0].id
        : removedAlternatives[0].id;
      const newGroup = genericModalDataToAlternativeData([...alternatives]);
      const updatedCombinations = [...internalData];
      const [matchedIndex, groupIndex] =
        getMatchedIndexByAlternative(alternativeId);
      /**
       * Verificação: Verifica o tipo de operação
       * case true: Criação de um novo grupo
       * case false: edição de um grupo
       */
      if (matchedIndex < 0) {
        const newCombinationIndex =
          updatedCombinations[groupIndex].matched.push([...newGroup]) - 1;
        removeAlternativeAvulse(selectedAlternativesRef.current, groupIndex);
      } else {
        /**
         * Verificação: Verifica se existe mais de 1 alternativas nas combinações
         * case true: atualiza a combinação
         * case false: remove uma combinação
         */
        if (alternatives.length > 1) {
          updatedCombinations[groupIndex].matched[matchedIndex] = newGroup;
          const parsedAvulse = genericModalDataToAlternativeData([
            ...removedAlternatives,
          ]);
          updatedCombinations[groupIndex].avulsas.push(...parsedAvulse);
        } else {
          updatedCombinations[groupIndex].matched.splice(matchedIndex, 1);
          const avulsesAlternatives = [...removedAlternatives, ...alternatives];
          const parsedAvulse =
            genericModalDataToAlternativeData(avulsesAlternatives);
          updatedCombinations[groupIndex].avulsas.push(...parsedAvulse);
        }
      }
      setInternalData(updatedCombinations);
      resetSelectedAlternatives();
      onCloseCombinationModal();
    },
    [resetSelectedAlternatives, showCombinationModal]
  );

  /**
   * Callbacks: Remove a(s) alternativa(s) selecionadas após envio ou combinação
   */
  const removeAlternativeAvulse = useCallback(
    (alternatives, index) => {
      const alternativesGroup = [...internalData];
      alternatives.map((alternative) => {
        const [, , findIndex] = getMatchedIndexByAlternative(alternative.id);
        alternativesGroup[index].avulsas.splice(findIndex, 1);
      });
      setInternalData(alternativesGroup);
    },
    [internalData]
  );

  function onCloseSendAlternativesModal() {
    setShowSendAlternativeModal(false);
  }

  // Abrir modal de edição de alternativas com as alternativas presente no grupo clicado
  const openEditCombinationModal = (groupId) => {
    const [matchedIndex, groupIndex] = getMatchedIndexByAlternative(groupId);
    const selectedGroup = alternativesToGenericModel([
      ...internalData[groupIndex].matched[matchedIndex],
    ]);
    selectedAlternativesRef.current = selectedGroup;
    setShowCombinationModal(true);
  };
  /**
   * Callbacks: Renderiza a modal de combinação de alternativas
   */
  const renderCombinationModal = useCallback(() => {
    if (showCombinationModal) {
      return (
        <CombinationAlternativeModal
          groupValues={selectedAlternativesRef.current}
          groupTitle={selectedAlternativesRef.current[0].mainTitle}
          onSave={onSaveCombinationAlternatives}
          onCancel={onCloseCombinationModal}
        />
      );
    }
    return null;
  }, [selectedAlternativesRef, showCombinationModal]);

  // Atualiza as alternativas combinadas após enviar uma(s) nova(s) alternativa(s)
  function handleUpdateGroup(selectedGroup) {
    // Recupera o indice do grupo que deverá ser alterado
    const [index, groupIndex] = getMatchedIndexByAlternative(selectedGroup.id);

    // Altera o objeto da alternativa
    const resultado = [...(selectedAlternativesRef.current || [])].map(
      (alternativa) => {
        const { groupId, surveyId, questionId, alternativeId } =
          getAlternativeIdentifiers(alternativa.id);

        return {
          enunciado_alternativa: alternativa.mainTitle,
          id_alternativa: alternativeId,
          id_grupo: groupId,
          id_pergunta: questionId,
          id_pesquisa: surveyId,
          titulo_pesquisa: alternativa.tagTitle,
        };
      }
    );

    const newData = internalData[groupIndex].matched[index];
    const groupIdList = newData.map((item) => item.id_pesquisa);

    const onSuccess = () => {
      newData.push(...resultado);
      // Atualiza os dados internos
      setInternalData(internalData);
      // remove as alternativas avulsas após combinação
      removeAlternativeAvulse(selectedAlternativesRef.current, groupIndex);
      // fecha modal após envio de alternativa para gruopo
      onCloseSendAlternativesModal();
      // reseta a referencia das alternativas selecionadas
      resetSelectedAlternatives();
      // Notifica a ação de sucesso
      showFlashMessage(
        ALTERNATIVE_SENT.title,
        ALTERNATIVE_SENT.message,
        'success'
      );
    };

    const onError = () => {
      const { title, message } = SEND_ALTERNATIVES_WITH_SAME_SURVEYID;
      showFlashMessage(title, message, 'warning');
    };

    // Verifica se a ação é possivel, caso true executa a callback de sucesso.
    handleOnExecuteCombinationActions(
      mapAlternativesToSurveyIdList(selectedAlternativesRef.current),
      groupIdList,
      onSuccess,
      onError,
    );
  }

  function getMatchedIndexByAlternative(id) {
    const {
      groupId,
      alternativeId,
      questionId,
      surveyId: altSurveyId,
    } = getAlternativeIdentifiers(id);
    const { groupIndex, matched, avulsas } =
      getParsedAlternativesByGroupId(groupId);

    const index = matched.findIndex((array) =>
      array.find(
        (item) =>
          item.id_alternativa == alternativeId &&
          item.id_pergunta === questionId &&
          item.id_pesquisa === altSurveyId
      )
    );
    const avulseIndex = avulsas.findIndex(
      (item) =>
        item.id_alternativa == alternativeId &&
        item.id_pergunta === questionId &&
        item.id_pesquisa === altSurveyId
    );

    return [
      // indice que referencia um objeto no array MATCHED de internalData
      index,
      // indice que referencia array principal (0) e secundário[1] de internaldata
      groupIndex,
      // indice que referencia um objeto no array AVULSE de internalData
      avulseIndex,
    ];
  }
  /**
   * Callbacks: Renderiza a modal de envio de alternativa para uma combinação
   */
  const renderSendAlternativesModal = useCallback(() => {
    if (showSendAlternativeModal) {
      const { matched: groupAlternatives } = getParsedAlternativesByGroupId(
        getCurrentGroupId()
      );

      return (
        <SendAlternativesCombinationModal
          groupValues={withParsedAlternatives(groupAlternatives)}
          onSave={handleUpdateGroup}
          onCancel={onCloseSendAlternativesModal}
          sentAlternatives={selectedAlternativesRef.current}
        />
      );
    }

    return null;
  }, [internalData, selectedAlternativesRef, showSendAlternativeModal]);

  return (
    <Ui.Container visible={visible}>
      <Tooltip toolTipTitle="Nas perguntas a seguir selecione alternativas semelhantes e crie combinações entre elas" />
      <Ui.CombinedList>
        <GenericCollapsibleList
          data={principal}
          customItemProps={{
            ...itemProps,
            isOpen: true,
            TagIcon: MatchedIcon,
            IconTitle: ListAlMenuIcon,
          }}
          withExpansibleItems
          ChildrenComponent={renderAlternativesList}
        />
      </Ui.CombinedList>
      <Ui.CombinedList>
        <GenericCollapsibleList
          data={secondary}
          itemsVisualized={visualized}
          customItemProps={{
            ...itemProps,
            TagIcon: MatchedIcon,
            IconTitle: ListAlMenuIcon,
          }}
          withExpansibleItems
          ChildrenComponent={renderAlternativesList}
          onExpandChanges={handleExpandItem}
        />
      </Ui.CombinedList>
      {renderCombinationModal()}
      {renderSendAlternativesModal()}
    </Ui.Container>
  );
}

export default React.memo(StepThree);
